import React, { useState } from 'react'
import * as PropTypes from 'prop-types'
import { useUser } from '../../../../../../context/userContext'
import { useTranslation } from 'react-i18next'
import { Button, message, Popconfirm } from 'antd'
import { useMutation } from '@apollo/client'
import { UPDATE_PATIENT_REFERENCE_ID } from '@dentolize/clinic-mobile/src/shared/store/mutations/patientMutations'
import { showAuthError } from '../../../../../../shared/utils/webHelpers'
import { useLoginModal } from '../../../../../../context/loginModalContext'
import PatientForm from '../../../PatientForm/PatientForm'

const UpdateReferenceIdButton = ({ patient, onSelectPatient, handleSelectLead, onConverted }) => {
  const { user } = useUser()
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()

  const [formVisible, setFormVisible] = useState(false)

  const [updatePatientId, { loading }] = useMutation(UPDATE_PATIENT_REFERENCE_ID)

  const handleSubmit = async () => {
    const requiredFields = user.company.requiredPatientFields

    // Check required fields
    if (
      (requiredFields.includes('phoneNumber2') && !patient.phoneNumber2) ||
      (requiredFields.includes('email') && !patient.email) ||
      (requiredFields.includes('patientId') && !patient.patientId) ||
      (requiredFields.includes('birthDate') && !patient.birthDay) ||
      // middle names are kept in the last name, before its last word
      (requiredFields.includes('middleNames') && (patient.lastName || '').trim().split(/\s+/).length < 2) ||
      (requiredFields.includes('age') && !patient.birthDate) ||
      (requiredFields.includes('patientTitle') && !patient.title) ||
      (requiredFields.includes('gender') && !patient.gender) ||
      (requiredFields.includes('address') && !patient.address) ||
      (requiredFields.includes('assignedPractitioner') && !patient.doctorId) ||
      (requiredFields.includes('priceList') && !patient.priceListId) ||
      (requiredFields.includes('job') && !patient.job) ||
      (requiredFields.includes('tax') && typeof patient.tax !== 'number') ||
      (requiredFields.includes('patientDetails') && !patient.details) ||
      (requiredFields.includes('insuranceDetails') && !patient.insuranceCompanyId) ||
      (requiredFields.includes('referralSource') && !patient.referralSourceId) ||
      (requiredFields.includes('marital') && !patient.marital) ||
      (requiredFields.includes('emergencyContact') && !patient.firstNameE) ||
      (requiredFields.includes('insuranceNumber') && !patient.insuranceNumber) ||
      (requiredFields.includes('referralDetails') && !patient.referralDetails) ||
      (requiredFields.includes('insuranceGroup') && !patient.insuranceGroup) ||
      (requiredFields.includes('insuranceCRT') && !patient.insuranceCRT) ||
      (requiredFields.includes('tags') && !patient.patientTags?.length) ||
      (requiredFields.includes('nationality') && !patient.nationality) ||
      (requiredFields.includes('nationalId') && !patient.nationalId)
    ) {
      message.info(t('app.somePatientFieldsRequired'))
      if (handleSelectLead) {
        handleSelectLead(patient)
      } else {
        setFormVisible(true)
      }
      return
    }

    try {
      const res = await updatePatientId({ variables: { patient: patient.id } })
      message.success(t('common.updatedSuccessfully'))
      if (onConverted) {
        onConverted(res.data.updatePatientReferenceId)
      }
      if (onSelectPatient) {
        onSelectPatient(patient.id)
      }
    } catch (e) {
      if (e?.message?.includes('Some required patient')) {
        if (handleSelectLead) {
          handleSelectLead(patient)
        } else {
          setFormVisible(true)
        }
      }
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  const handleClose = () => {
    setFormVisible(false)
  }

  return patient.referenceId === 0 || patient.type === 2 ? (
    <div onClick={e => e.stopPropagation()}>
      <Popconfirm
        title={t(patient.type === 2 ? 'app.convertToPatient' : 'app.assignNewID')}
        onConfirm={handleSubmit}
        disabled={loading || (!user.permissions.addNewPatient && !user.permissions.editPatient)}
        okText={t('common.yes')}
        cancelText={t('common.no')}
      >
        <Button
          type="primary"
          size="small"
          loading={loading}
          disabled={!user.permissions.addNewPatient && !user.permissions.editPatient}
          danger
        >
          {t(patient.type === 2 ? 'app.convertToPatient' : 'app.assignNewID')}
        </Button>
      </Popconfirm>
      <PatientForm assignNewID formVisible={formVisible} handleClose={handleClose} selectedItem={patient} />
    </div>
  ) : null
}

UpdateReferenceIdButton.propTypes = {
  patient: PropTypes.object.isRequired,
  onSelectPatient: PropTypes.func,
  handleSelectLead: PropTypes.func,
  onConverted: PropTypes.func
}

export default UpdateReferenceIdButton
